import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, Package, Move, Eye, Edit, Laptop, Projector, Microscope, Maximize, Minimize, GripVertical, Download } from "lucide-react";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { motion } from "framer-motion";
import { Rnd } from "react-rnd";
import html2canvas from "html2canvas";
import { db } from "../../services/firebase";

// Get appropriate icon based on asset category
const getAssetIcon = (category) => {
  switch (category) {
    case "ict-equipment":
      return Laptop;
    case "devices":
      return Projector;
    case "laboratory-equipment":
      return Microscope;
    default:
      return Package; // Fallback for unknown categories
  }
};

// --- Room Component ---
const RoomBox = ({ room, onDataChange, isEditMode, matchedAssets, onDropAsset, onClick, selectedRoom, containerRect }) => {
  // Compute position and size dynamically from props to avoid state sync issues
  const position = containerRect ? {
    x: (room.left / 100) * containerRect.width,
    y: (room.top / 100) * containerRect.height,
  } : { x: 0, y: 0 };

  const size = containerRect ? {
    width: (room.width / 100) * containerRect.width,
    height: (room.height / 100) * containerRect.height,
  } : { width: 0, height: 0 };

  const handleDragStop = (e, d) => {
    if (!containerRect) return;
    const newX = (d.x / containerRect.width) * 100;
    const newY = (d.y / containerRect.height) * 100;
    onDataChange({ x: newX, y: newY, width: room.width, height: room.height });
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    if (!containerRect) return;
    const newWidth = (ref.offsetWidth / containerRect.width) * 100;
    const newHeight = (ref.offsetHeight / containerRect.height) * 100;
    const newX = (position.x / containerRect.width) * 100;
    const newY = (position.y / containerRect.height) * 100;
    onDataChange({ x: newX, y: newY, width: newWidth, height: newHeight });
  };

  if (isEditMode) {
    return (
      <Rnd
        position={position}
        size={size}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        bounds="parent"
        className="border-2 border-blue-600 bg-blue-100/30 rounded-sm"
        minWidth={20}
        minHeight={20}
      >
        <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-800 cursor-move">
          {room.name} <Move size={10} className="ml-1" />
        </div>
      </Rnd>
    );
  }

  const isSelected = selectedRoom?.id === room.id;

  return (
    <div
      className={`absolute rounded-sm cursor-pointer transition-all duration-200 ${
        isSelected ? "border-2 border-blue-500 bg-blue-100/50" : "hover:border-2 hover:border-blue-300 hover:bg-blue-50/30"
      }`}
      style={{
        top: `${room.top}%`,
        left: `${room.left}%`,
        width: `${room.width}%`,
        height: `${room.height}%`,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(room);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => onDropAsset(e, room)}
    >
      {matchedAssets.map((a, i) => {
        const IconComponent = getAssetIcon(a.category);
        return (
          <motion.div
            key={a.id}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("assetId", a.id)}
          className="absolute bg-indigo-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow"
          style={{ top: `${3 + i * 8}px`, left: `${3}px` }}
          title={a.name}
        >
          <IconComponent size={10} />
          </motion.div>
        );
      })}
    </div>
  );
};

// --- Main Map Editor ---
export default function MapEditor() {
  const getStatusColor = (status) => {
    switch (status) {
      case "available": return "bg-green-100 text-green-800";
      case "in-use": return "bg-blue-100 text-blue-800";
      case "maintenance": return "bg-yellow-100 text-yellow-800";
      case "depreciated": return "bg-orange-100 text-orange-800";
      case "for-disposal": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const [rooms, setRooms] = useState([]);
  const [assets, setAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [containerRect, setContainerRect] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAllAssets, setShowAllAssets] = useState(false);
  const [showAssetsOnMap, setShowAssetsOnMap] = useState(false);
  const [assetCategoryFilter, setAssetCategoryFilter] = useState("all");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  }));
  const containerRef = useRef(null);

  const categoryOptions = [
    { value: "all", label: "All Categories" },
    { value: "ict-equipment", label: "ICT Equipment" },
    { value: "devices", label: "Devices" },
    { value: "laboratory-equipment", label: "Laboratory Equipment" },
  ];

  // Track container size using ResizeObserver for better responsiveness
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateRect = () => {
      if (containerRef.current) {
        setContainerRect(containerRef.current.getBoundingClientRect());
      }
    };

    // Initial update
    updateRect();

    // Use ResizeObserver for more reliable container size tracking
    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(element);

    // Also listen to window resize as fallback
    window.addEventListener("resize", updateRect);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateRect);
    };
  }, []);

  // Force container rect update when fullscreen mode changes
  useEffect(() => {
    // Small delay to ensure DOM has updated after fullscreen toggle
    const timeoutId = setTimeout(() => {
      if (containerRef.current) {
        setContainerRect(containerRef.current.getBoundingClientRect());
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isFullscreen]);

  // Track floorplan background image loading to ensure proper positioning
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const img = new Image();
      img.onload = () => {
        // Force container rect update when floorplan loads
        if (containerRef.current) {
          setContainerRect(containerRef.current.getBoundingClientRect());
        }
      };
      img.src = '/CathsFloorplan.png';
    }
  }, []);

  // Ensure positioning updates after DOM settle in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      // Multiple checks to ensure stable positioning
      const timeouts = [
        setTimeout(() => {
          if (containerRef.current) setContainerRect(containerRef.current.getBoundingClientRect());
        }, 50),
        setTimeout(() => {
          if (containerRef.current) setContainerRect(containerRef.current.getBoundingClientRect());
        }, 150),
        setTimeout(() => {
          if (containerRef.current) setContainerRect(containerRef.current.getBoundingClientRect());
        }, 300)
      ];

      return () => timeouts.forEach(clearTimeout);
    }
  }, [isFullscreen]);

  // Firestore data sync
  useEffect(() => {
    const unsubRooms = onSnapshot(collection(db, "rooms"), (snap) =>
      setRooms(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubAssets = onSnapshot(collection(db, "assets"), (snap) =>
      setAssets(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => {
      unsubRooms();
      unsubAssets();
    };
  }, []);

  // Update search results instantly
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    const t = searchTerm.toLowerCase();
    const categoryFilteredAssets = assetCategoryFilter === "all"
      ? assets
      : assets.filter((a) => a.category === assetCategoryFilter);

    setSearchResults(
      categoryFilteredAssets.filter(
        (a) =>
          a.name?.toLowerCase().includes(t) ||
          a.serialNumber?.toLowerCase().includes(t)
      )
    );
  }, [assets, searchTerm, assetCategoryFilter]);

  // Track window size for responsive popup positioning
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle room edits
  const handleRoomChange = (id, d) => {
    setRooms((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, left: d.x, top: d.y, width: d.width, height: d.height } : r
      )
    );
  };

  // Save room layout
  const saveRoomData = async () => {
    setIsSaving(true);
    try {
      for (const room of rooms) {
        await updateDoc(doc(db, "rooms", room.id), {
          left: room.left,
          top: room.top,
          width: room.width,
          height: room.height,
        });
      }
      alert("✅ Layout saved! Boxes hidden for clean floorplan view.");
      setIsEditMode(false);
    } catch (err) {
      alert("❌ Failed to save layout: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Drop asset into room
  const handleDropAsset = async (e, room) => {
    const assetId = e.dataTransfer.getData("assetId");
    if (!assetId) return;
    setAssets((prev) =>
      prev.map((a) => (a.id === assetId ? { ...a, location: room.id } : a))
    );
    try {
      await updateDoc(doc(db, "assets", assetId), { location: room.id });
    } catch (err) {
      alert("❌ Failed to move asset: " + err.message);
    }
  };

  // Handle asset click - find and select the room containing that asset
  const handleAssetClick = (asset) => {
    if (asset.location) {
      const room = rooms.find(r => r.id === asset.location);
      if (room) {
        setSelectedRoom(room);
        setSelectedAsset(asset);
      }
    }
  };

  // Download map as image
  const downloadMap = async () => {
    const element = containerRef.current;
    if (!element) {
      alert("Map container not found!");
      return;
    }

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        width: element.offsetWidth,
        height: element.offsetHeight,
      });

      const link = document.createElement('a');
      link.download = `facility-map-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error("Error downloading map:", error);
      alert("Failed to download map. Please try again.");
    }
  };

  // Toggle fullscreen function
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Keyboard event listener for F key
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only trigger fullscreen if 'F' key is pressed and not in edit mode
      if (event.key.toLowerCase() === 'f' && !isEditMode) {
        // Check if any input-like elements are focused (search input, etc.)
        const activeElement = document.activeElement;
        const isInputFocused = activeElement &&
          (activeElement.tagName === 'INPUT' ||
           activeElement.tagName === 'TEXTAREA' ||
           activeElement.tagName === 'SELECT' ||
           activeElement.isContentEditable);

        // Only prevent default and toggle fullscreen if no input is focused
        if (!isInputFocused) {
          event.preventDefault();
          toggleFullscreen();
        }
        // If input is focused, let the 'F' key work normally for typing
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isEditMode, toggleFullscreen]);

  // Filter for floating assets (not in rooms)
  let filteredAssetsForFloating = showAssetsOnMap ? assets : searchResults;
  // Apply category filter when showing all assets on map
  if (showAssetsOnMap && assetCategoryFilter !== "all") {
    filteredAssetsForFloating = assets.filter((a) => a.category === assetCategoryFilter);
  }
  const floatingAssets = filteredAssetsForFloating.filter(
    (a) => !a.location || !rooms.find((r) => r.id === a.location)
  );

  return (
    <div className={`p-6 bg-gray-50 min-h-screen ${isFullscreen ? 'fixed inset-0 z-50 bg-black p-4' : ''}`}>
      {/* Header - Hidden in fullscreen */}
      {!isFullscreen && (
        <div className="flex justify-between mb-4 bg-white p-4 rounded shadow">
          <div>
            <h1 className="text-2xl font-bold">
              Facility Map {isEditMode ? "Editor" : "Viewer"}
            </h1>
            <p className="text-sm text-gray-600">
              {isEditMode
                ? "Resize and drag rooms smoothly to adjust layout."
                : "Search and drag assets across rooms."}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={downloadMap}
              className="px-3 py-2 rounded text-sm bg-purple-600 text-white hover:bg-purple-700"
              title="Download map as image"
            >
              <Download size={16} />
            </button>
            <button
              onClick={() => setShowAssetsOnMap(!showAssetsOnMap)}
              className={`px-3 py-2 rounded text-sm ${
                showAssetsOnMap ? "bg-green-600 text-white" : "bg-gray-100"
              }`}
              title="Show assets on map"
            >
              Show Assets
            </button>
            {!isEditMode && showAssetsOnMap && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Category:</label>
                <select
                  value={assetCategoryFilter}
                  onChange={(e) => setAssetCategoryFilter(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  style={{ minWidth: '140px' }}
                >
                  {categoryOptions.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isEditMode ? (
              <>
                <button
                  onClick={saveRoomData}
                  disabled={isSaving}
                  className="bg-green-600 text-white px-4 py-2 rounded"
                >
                  {isSaving ? "Saving..." : "Save Layout"}
                </button>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded"
                >
                  <Eye size={16} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditMode(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                <Edit size={16} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search - Hidden in fullscreen */}
      {!isEditMode && !isFullscreen && (
        <div className="flex mb-3 justify-between items-center">
          <div className="relative w-full max-w-lg">
            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search asset by name or serial..."
              className="w-full border rounded pl-8 pr-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            title={isFullscreen ? "Exit fullscreen (F)" : "Enter fullscreen (F)"}
          >
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
        </div>
      )}

      {/* Fullscreen Map Only */}
      {isFullscreen ? (
        <div className="relative w-full h-full">
          {/* Exit Fullscreen Button */}
          <button
            onClick={toggleFullscreen}
            className={`absolute top-2 right-2 z-10 flex items-center gap-1 bg-red-600 text-white rounded shadow-lg hover:bg-red-700 transition-colors ${
              windowSize.width < 640 ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
            }`}
            title="Exit fullscreen (F)"
          >
            <Minimize size={windowSize.width < 640 ? 12 : 14} />
            <span className={`${windowSize.width < 640 ? 'hidden' : ''}`}>Exit Fullscreen</span>
          </button>

          {/* Map Container */}
          {(() => {
            // Check if mobile landscape (small screen, width > height) for scrolling
            const isMobileLandscape = windowSize.width < 1024 && windowSize.width > windowSize.height;

            return (
              <div
                ref={containerRef}
                className={`relative border-2 border-gray-300 rounded w-full ${
                  isMobileLandscape ? 'overflow-auto h-full' : 'overflow-hidden'
                }`}
                style={{
                  paddingBottom: "50%" // Keep consistent aspect ratio for proper room positioning
                }}
              >
                <div
                  className="absolute inset-0 bg-center bg-no-repeat bg-contain"
                  style={{
                    backgroundImage: "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px), url('/CathsFloorplan.png')",
                    backgroundSize: "20px 20px, 20px 20px, contain",
                    backgroundBlendMode: "overlay"
                  }}
                >
                  {rooms.map((r) => {
                    let roomAssets = showAssetsOnMap ? assets : searchResults;
                    // Apply category filter when showing all assets on map
                    if (showAssetsOnMap && assetCategoryFilter !== "all") {
                      roomAssets = assets.filter((a) => a.category === assetCategoryFilter);
                    }
                    return (
                      <RoomBox
                        key={r.id}
                        room={r}
                        onDataChange={(d) => handleRoomChange(r.id, d)}
                        isEditMode={isEditMode}
                        matchedAssets={roomAssets.filter((a) => a.location === r.id)}
                        onDropAsset={handleDropAsset}
                        onClick={!isEditMode ? (room) => setSelectedRoom(selectedRoom?.id === room.id ? null : room) : null}
                        selectedRoom={selectedRoom}
                        containerRect={containerRect}
                      />
                    );
                  })}

                  {/* Floating assets */}
                  {!isEditMode &&
                    floatingAssets.map((a, i) => {
                      const IconComponent = getAssetIcon(a.category);
                      return (
                        <motion.div
                          key={a.id}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData("assetId", a.id)}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="absolute bg-indigo-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow"
                          style={{
                            top: `${10 + i * 15}px`,
                            left: "10px",
                          }}
                          title={a.name}
                        >
                          <IconComponent size={10} />
                        </motion.div>
                      );
                    })}
                </div>
              </div>
            );
          })()}

          {/* Room Assets Popup in Fullscreen */}
          {selectedRoom && selectedRoom !== null && (
            <Rnd
              key={`${windowSize.width}-${windowSize.height}`} // Force re-render on window resize
              default={{
                x: windowSize.width < 1024
                  ? Math.max(8, windowSize.width - 220) // Small screens: position near right edge to block less
                  : Math.min(16, windowSize.width - 340), // Large screens: keep original positioning
                y: windowSize.width < 1024
                  ? Math.max(60, windowSize.height - 280) // Small screens: position near bottom
                  : Math.min(100, windowSize.height - 200), // Large screens: keep original
                width: windowSize.width < 1024
                  ? Math.min(windowSize.width * 0.6, 180) // Small screens: 60% width, max 180px (compact)
                  : Math.min(windowSize.width * 0.8, 320), // Large screens: 80% width, max 320px
                height: 'auto'
              }}
              bounds="window"
              disableResizing={true}
              className="z-20"
              dragHandleClassName="drag-handle"
              minWidth={180}
              maxWidth={windowSize.width < 1024 ? 200 : 400}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto w-full"
              >
                <div className="drag-handle p-3 bg-gray-50 border-b border-gray-200 cursor-move flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">{selectedRoom.name}</h3>
                    <p className="text-xs text-gray-600">Click assets to drag & reposition</p>
                  </div>
                  <GripVertical size={16} className="text-gray-500" />
                </div>
                <div className="p-2 space-y-2">
                  {(() => {
                    // In fullscreen mode, always show all assets in the room
                    let roomAssets = assets;
                    if (assetCategoryFilter !== "all") {
                      roomAssets = assets.filter((a) => a.category === assetCategoryFilter);
                    }
                    return roomAssets.filter((a) => a.location === selectedRoom.id);
                  })().map((asset) => {
                    const IconComponent = getAssetIcon(asset.category);
                    return (
                      <motion.div
                        key={asset.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("assetId", asset.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        <div className="bg-indigo-600 text-white rounded-full p-1">
                          <IconComponent size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm">{asset.name}</div>
                          <div className="text-xs text-gray-500">{asset.serialNumber || 'No serial'}</div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                          {asset.status}
                        </span>
                      </motion.div>
                    );
                  })}
                  {(() => {
                    // In fullscreen mode, always show all assets in the room
                    let roomAssets = assets;
                    if (assetCategoryFilter !== "all") {
                      roomAssets = assets.filter((a) => a.category === assetCategoryFilter);
                    }
                    return roomAssets.filter((a) => a.location === selectedRoom.id);
                  })().length === 0 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No assets in this room
                    </div>
                  )}
                </div>
              </motion.div>
            </Rnd>
          )}
        </div>
      ) : (
        /* Normal View */
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Floorplan */}
          <div className="lg:col-span-3">
            <div className="bg-white p-4 rounded shadow-inner">
              <div
                ref={containerRef}
                className="relative border-2 border-gray-300 rounded overflow-hidden"
                style={{ paddingBottom: "50%" }}
              >
                <div
                  className="absolute inset-0 bg-center bg-no-repeat bg-contain"
                  style={{
                    backgroundImage: "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px), url('/CathsFloorplan.png')",
                    backgroundSize: "20px 20px, 20px 20px, contain",
                    backgroundBlendMode: "overlay",
                  }}
                >
                  {rooms.map((r) => {
                    let roomAssets = showAssetsOnMap ? assets : searchResults;
                    // Apply category filter when showing all assets on map
                    if (showAssetsOnMap && assetCategoryFilter !== "all") {
                      roomAssets = assets.filter((a) => a.category === assetCategoryFilter);
                    }
                    return (
                      <RoomBox
                        key={r.id}
                        room={r}
                        onDataChange={(d) => handleRoomChange(r.id, d)}
                        isEditMode={isEditMode}
                        matchedAssets={roomAssets.filter((a) => a.location === r.id)}
                        onDropAsset={handleDropAsset}
                        onClick={!isEditMode ? (room) => setSelectedRoom(selectedRoom?.id === room.id ? null : room) : null}
                        selectedRoom={selectedRoom}
                        containerRect={containerRect}
                      />
                    );
                  })}

                  {/* Floating assets */}
                  {!isEditMode &&
                    floatingAssets.map((a, i) => {
                      const IconComponent = getAssetIcon(a.category);
                      return (
                        <motion.div
                          key={a.id}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData("assetId", a.id)}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="absolute bg-indigo-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow"
                          style={{
                            top: `${10 + i * 15}px`,
                            left: "10px",
                          }}
                          title={a.name}
                        >
                          <IconComponent size={10} />
                        </motion.div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          {/* Assets Sidebar */}
          {!isEditMode && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded shadow p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Assets Overview</h3>
                  <button
                    onClick={() => setShowAllAssets(!showAllAssets)}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                  >
                    {showAllAssets ? "Show Summary" : "Show All Assets"}
                  </button>
                </div>

                {/* All Assets */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">All Assets</h4>
                  <div className={`space-y-1 overflow-y-auto ${showAllAssets ? 'max-h-96' : 'max-h-32'}`}>
                    {(showAllAssets ? assets : assets.slice(0, 10)).map((asset) => (
                      <div
                        key={asset.id}
                        className={`flex justify-between items-center text-xs cursor-pointer rounded px-1 hover:bg-gray-100 transition-colors ${
                          selectedAsset?.id === asset.id ? 'bg-blue-50 border border-blue-200' : ''
                        }`}
                        onClick={() => handleAssetClick(asset)}
                      >
                        <span className="text-gray-700 truncate flex-1 mr-2">
                          {asset.name}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                          {asset.status}
                        </span>
                      </div>
                    ))}
                    {!showAllAssets && assets.length > 10 && (
                      <div className="text-xs text-gray-500 text-center py-1">
                        ...and {assets.length - 10} more (Click "Show All Assets" to see everything)
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Room Assets */}
                {selectedRoom && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Assets in {selectedRoom.name}
                    </h4>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {assets.filter(asset => asset.location === selectedRoom.id).map((asset) => (
                        <div
                          key={asset.id}
                          className={`flex justify-between items-center text-xs cursor-pointer rounded px-1 hover:bg-gray-100 transition-colors ${
                            selectedAsset?.id === asset.id ? 'bg-blue-50 border border-blue-200' : ''
                          }`}
                          onClick={() => handleAssetClick(asset)}
                        >
                          <span className="text-gray-700 truncate flex-1 mr-2">
                            {asset.name}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                            {asset.status}
                          </span>
                        </div>
                      ))}
                      {assets.filter(asset => asset.location === selectedRoom.id).length === 0 && (
                        <div className="text-xs text-gray-500 text-center py-2">
                          No assets in this room
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-600">
                    <div>Total Assets: {assets.length}</div>
                    <div>Rooms: {rooms.length}</div>
                    {selectedRoom && (
                      <div>Selected: {selectedRoom.name}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
